/**
 * API Client Service
 * Provides typed HTTP client for backend communication
 */
import axios from 'axios';
import { supabase } from '@/lib/supabase';
import { isEmbedMode, getEmbedToken } from '@/lib/embedMode';
const vueAppApiUrlBe = process.env.VUE_APP_API_URL_BE;
if (!vueAppApiUrlBe) {
    throw new Error('[DEBUG][client.tx][ENV ERROR] VUE_APP_API_URL_BE is not defined in environment variables');
}
const api = axios.create({
    baseURL: vueAppApiUrlBe,
    headers: {
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true' // This header is used to skip the ngrok browser warning
    },
    timeout: 5000, // Set a timeout of 5 seconds for requests
});
// api.defaults.headers.common['User-Agent'] = 'MyCustomClient/1.0';         // 👈 Header #2
api.defaults.headers.common['ngrok-skip-browser-warning'] = 'true'; // 👈 Header #1
// Add auth token interceptor
api.interceptors.request.use(async (config) => {
    try {
        // Sparkco embed mode (Task A6): the URL-minted sparkco token IS the
        // credential — attach it and never touch Supabase.
        if (isEmbedMode()) {
            const embedToken = getEmbedToken();
            if (embedToken) {
                config.headers.Authorization = `Bearer ${embedToken}`;
            }
            return config;
        }
        // Get current session from Supabase
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
            // Silently fail - user might not be logged in
            return config;
        }
        if (session?.access_token) {
            // Add Authorization header with JWT token
            config.headers.Authorization = `Bearer ${session.access_token}`;
            // Debug log (comment out for production)
            // console.log('[API Client] Auth token added to request:', config.url);
        }
        return config;
    }
    catch (error) {
        // Silently fail on error
        return config;
    }
}, (error) => {
    return Promise.reject(error);
});
export default api;
/**
 * Fetches all sessions (full data with transcripts)
 * Use this when you need complete session data
 * NOTE: This is slow (~3.5s for 143 sessions, ~572KB payload)
 */
export async function listSessions() {
    console.log(`[DEBUG-1000] [client.ts] [listSessions()] Fetching all sessions from the backend`);
    console.log('[DEBUG-1001] [client.ts] [listSessions()] API URL:', api.defaults.baseURL);
    const { data } = await api.get('/sessions');
    /* ───── 1) Normal case – array of sessions ───── */
    if (Array.isArray(data)) {
        console.log(`[DEBUG-1002] [client.ts] [listSessions()] ARRAY - from full URL: ${api.defaults.baseURL}/api/sessions`);
        console.log(`[DEBUG-1002] [client.ts] [listSessions()] ARRAY - Fetched ${data.length} sessions`);
        console.log(`[DEBUG-1003] [client.ts] [listSessions()] ARRAY - Sessions:`, data);
        return data;
    }
    /* ───── 2) Unexpected HTML / error page ───── */
    if (typeof data === 'string') {
        console.log(`[DEBUG-1101] [client.ts] [listSessions()] STRING - Sessions:`, data);
        console.error('[ERROR] Backend returned HTML instead of JSON');
        // a. Create a Blob containing the HTML.
        const blob = new Blob([data], { type: 'text/html' });
        // b. Build an object-URL and trigger a download.
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'error_response.html';
        document.body.appendChild(a);
        a.click(); // ← opens the "Save" dialog
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        throw new Error('API returned HTML; see error_response.html for details.');
    }
    /* ───── 3) Some other JSON object (unexpected) ───── */
    console.error('[ERROR] Unknown response shape:', data);
    throw new Error('API returned unexpected JSON (not Session[]).');
}
/**
 * Fetches all sessions (lightweight - no transcripts)
 * Use this for list views and session browsing
 * PERFORMANCE: Fast (~400ms for 143 sessions, ~50KB payload)
 *
 * Full session data (with transcripts) can be loaded on-demand when user opens a session
 */
export async function listSessionsLightweight(organizationId) {
    console.log(`[DEBUG-1050] [client.ts] [listSessionsLightweight()] Fetching lightweight sessions from the backend${organizationId ? ` for org ${organizationId}` : ''}`);
    console.log('[DEBUG-1051] [client.ts] [listSessionsLightweight()] API URL:', api.defaults.baseURL);
    const startTime = performance.now();
    const url = organizationId ? `/sessions/list?organizationId=${organizationId}` : '/sessions/list';
    const { data } = await api.get(url);
    const loadTime = performance.now() - startTime;
    /* ───── 1) Normal case – array of sessions ───── */
    if (Array.isArray(data)) {
        const estimatedSize = data.length * 400; // ~400 bytes per lightweight session
        const sizeKB = Math.round(estimatedSize / 1024);
        console.log(`[DEBUG-1052] [client.ts] [listSessionsLightweight()] ARRAY - Fetched ${data.length} lightweight sessions (~${sizeKB}KB) in ${Math.round(loadTime)}ms`);
        console.log(`[DEBUG-1053] [client.ts] [listSessionsLightweight()] ARRAY - Sessions:`, data);
        return data;
    }
    /* ───── 2) Unexpected HTML / error page ───── */
    if (typeof data === 'string') {
        console.log(`[DEBUG-1151] [client.ts] [listSessionsLightweight()] STRING - Sessions:`, data);
        console.error('[ERROR] Backend returned HTML instead of JSON');
        const blob = new Blob([data], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'error_response.html';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        throw new Error('API returned HTML; see error_response.html for details.');
    }
    /* ───── 3) Some other JSON object (unexpected) ───── */
    console.error('[ERROR] Unknown response shape:', data);
    throw new Error('API returned unexpected JSON (not Session[]).');
}
export async function createSession(project, category, name, text) {
    console.log(`[DEBUG-1000] [client.ts] [createSession()] Creating session with project: "${project}", category: "${category}", name: "${name}"`);
    // Validate input parameters
    const { data } = await api.post('/sessions', {
        "text": text,
        "project": project,
        "category": category,
        "name": name,
        "session_id": "-1" // Use "-1" as a placeholder for the ID, which will be auto-generated by the backend
    });
    console.log(`[DEBUG-1001] [client.ts] [createSession()] Created session:`, data);
    return data;
}
export async function getRecentSessions(limit) {
    console.log(`[DEBUG-2000] [client.ts] [getRecentSessions()] Fetching ${limit} most recent sessions`);
    const { data } = await api.get(`/sessions/recent?limit=${limit}`);
    if (Array.isArray(data)) {
        console.log(`[DEBUG-2001] [client.ts] [getRecentSessions()] Fetched ${data.length} recent sessions`);
        return data;
    }
    if (typeof data === 'string') {
        console.error('[ERROR] Backend returned HTML instead of JSON for recent sessions');
        throw new Error('API returned HTML; expected JSON array of sessions.');
    }
    console.error('[ERROR] Unknown response shape for recent sessions:', data);
    throw new Error('API returned unexpected JSON (not Session[]).');
}
export async function getSessionsByCategory(project, category) {
    console.log(`[DEBUG-2100] [client.ts] [getSessionsByCategory()] Fetching sessions for project: "${project}", category: "${category}"`);
    const { data } = await api.get(`/sessions/by-category?project=${encodeURIComponent(project)}&category=${encodeURIComponent(category)}`);
    if (Array.isArray(data)) {
        console.log(`[DEBUG-2101] [client.ts] [getSessionsByCategory()] Fetched ${data.length} sessions for category`);
        return data;
    }
    if (typeof data === 'string') {
        console.error('[ERROR] Backend returned HTML instead of JSON for category sessions');
        throw new Error('API returned HTML; expected JSON array of sessions.');
    }
    console.error('[ERROR] Unknown response shape for category sessions:', data);
    throw new Error('API returned unexpected JSON (not Session[]).');
}
export async function getSessionMetadata() {
    console.log(`[DEBUG-2200] [client.ts] [getSessionMetadata()] Fetching session metadata`);
    const { data } = await api.get('/sessions/metadata');
    console.log(`[DEBUG-2201] [client.ts] [getSessionMetadata()] Fetched metadata:`, data);
    return data;
}
// ---------------------------------------------------------------------------
// Sparkco per-line bookmarks (Task A10, STRETCH) — sparkco-auth (embed) only.
// The BE answers 503 {error:'bookmarks_unavailable'} when the optional
// sparkco_bookmarks table has not been created — callers treat that as
// "feature off" and hide the affordance.
// ---------------------------------------------------------------------------
/** Bookmarked line numbers for the CURRENT user in one sparkco session. */
export async function getSparkcoBookmarks(sessionUid) {
    const { data } = await api.get(`/sparkco/bookmarks?session_uid=${encodeURIComponent(sessionUid)}`);
    return Array.isArray(data?.bookmarks) ? data.bookmarks : [];
}
export async function putSparkcoBookmark(sessionUid, lineNo) {
    await api.put('/sparkco/bookmarks', { session_uid: sessionUid, line_no: lineNo });
}
export async function deleteSparkcoBookmark(sessionUid, lineNo) {
    await api.delete('/sparkco/bookmarks', { data: { session_uid: sessionUid, line_no: lineNo } });
}
